// 机器详情页：Tabs（基础 / cache 策略 / 代理 / 槽位 / 模块 / 备注 / 事件 / 审计）。
// 数据源：GET /api/machines/{id}（machine/auths/caches/events/audit/online_agents）
//        + GET /api/machines/{id}/caches（cache 表格 + apply 历史）
//        + GET /api/proxy-profiles + GET /api/launch-slots?agent_id=...
// 写入：
//   - cache 策略单行/批量 apply；批量超过 20 行时自动附带确认 nonce
//   - sing-box apply、槽位代理绑定、槽位总容量；上游代理池在 /proxy-profiles 统一维护
//   - 备注编辑通过 InlineEditableCell

import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { RefreshCw, RotateCcw } from "lucide-react";
import {
  apiPost,
  apiGet,
  apiPostForm,
  apiPostURLSearchParams,
  APIError,
} from "@/api/client";
import {
  agentLaunchCapacityPath,
  agentSingBoxAutoAssignPath,
  agentSingBoxApplyPath,
  agentSingBoxHistoryPath,
  agentSingBoxPlanPath,
  agentSingBoxRollbackPath,
  agentSingBoxStatePath,
  agentSingBoxVersionsPath,
  cachePolicyBatchApplyPath,
  cachePolicyClearPath,
  cachePolicyPath,
  cachePolicyApplyPath,
  gameClientCapacitiesPath,
  gameClientCapacityPath,
  launchJobPath,
  launchSlotPath,
  launchSlotGameCapacityPath,
  launchSlotGameCapacitiesPath,
  launchSlotProbeExitIPPath,
  launchSlotRefreshStatusPath,
  launchSlotsPath,
  machineAuthMetadataPath,
  machineDetailPath,
  machineCachesPath,
  machineMarketCDPPath,
  proxyProfilesPath,
  roleLaunchBindingRestartPath,
  roleLaunchBindingStopPath,
  roleLaunchBindingsPath,
} from "@/api/endpoints";
import type {
  AgentLaunchCapacityResponse,
  AgentSingBoxApplyResponse,
  AgentSingBoxAutoAssignResponse,
  AgentSingBoxHistoryResponse,
  AgentSingBoxPlanResponse,
  AgentSingBoxRollbackResponse,
  AgentSingBoxStateResponse,
  AgentSingBoxVersionsResponse,
  GameClientCapacitiesResponse,
  GameClientCapacityEntry,
  GameClientCapacitySaveResponse,
  LaunchJobEntry,
  LaunchJobResponse,
  LaunchSlotEntry,
  LaunchSlotProbeExitIPResponse,
  LaunchSlotRefreshStatusResponse,
  LaunchSlotSaveResponse,
  LaunchSlotsResponse,
  MachineDetailResponse,
  MachineCachesResponse,
  MachineCacheEntry,
  MachineListEntry,
  MachineMarketCDPResponse,
  MachineRecordEntry,
  ProxyProfileEntry,
  ProxyProfilesResponse,
  RoleLaunchBindingActionResponse,
  RoleLaunchBindingEntry,
  RoleLaunchBindingsResponse,
  SlotGameCapacitiesResponse,
  SlotGameCapacityEntry,
  SlotGameCapacitySaveResponse,
} from "@/api/types";
import { requestConfirmNonce } from "@/api/confirm";
import { QueryView } from "@/components/ui/QueryView";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Checkbox } from "@/components/ui/Checkbox";
import { Badge } from "@/components/ui/Badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/Tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { StatusBadge, type StatusVariant } from "@/components/ui/StatusBadge";
import { Switch } from "@/components/ui/Switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { AgentCommandButton } from "@/components/forms/AgentCommandButton";
import { ConfirmDialog } from "@/components/forms/ConfirmDialog";
import { InlineEditableCell } from "@/components/forms/InlineEditableCell";
import { AuthActionsCell } from "@/components/forms/AuthActionsCell";
import { LauncherHooksPanel } from "@/components/machines/LauncherHooksPanel";
import { JobStatus } from "@/components/jobs/JobStatus";
import { useSession } from "@/hooks/useSession";
import { toast } from "@/components/ui/Sonner";
import { formatMs } from "@/lib/format";
import { refreshProxyQueries } from "@/lib/proxyFlowQueries";

export const Route = createFileRoute("/machines/$machineId")({
  component: MachineDetailRoute,
});

function MachineDetailRoute() {
  const { machineId } = useParams({ from: "/machines/$machineId" });
  return <MachineDetailView machineId={machineId} />;
}

export function MachineDetailView({ machineId }: { machineId: string }) {
  const session = useSession();
  const canWrite = session.data?.can_write ?? false;
  const actor = session.data?.actor ?? "anonymous";
  const queryClient = useQueryClient();

  const [tab, setTab] = useState("base");
  const q = useQuery({
    queryKey: ["machine", machineId],
    queryFn: () =>
      apiGet<MachineDetailResponse>(machineDetailPath(machineId)),
  });
  const cachesQ = useQuery({
    queryKey: ["machine-caches", machineId],
    queryFn: () =>
      apiGet<MachineCachesResponse>(machineCachesPath(machineId)),
    enabled: tab === "caches",
  });
  const isProxyTab = tab === "proxy";
  const isSlotsTab = tab === "slots";
  const needsAgentProxyData = isProxyTab || isSlotsTab;
  const proxyProfilesQ = useQuery({
    queryKey: ["proxy-profiles"],
    queryFn: () => apiGet<ProxyProfilesResponse>(proxyProfilesPath),
    enabled: needsAgentProxyData,
  });
  const agentID = dataAgentKey(q.data);
  const launchSlotsQ = useQuery({
    queryKey: ["launch-slots", agentID],
    queryFn: () => apiGet<LaunchSlotsResponse>(launchSlotsPath(agentID)),
    enabled: isSlotsTab && Boolean(agentID),
  });
  const roleBindingsQ = useQuery({
    queryKey: [
      "role-launch-bindings",
      agentID,
      launchSlotsQ.data?.items.map((slot) => slot.id).join(",") ?? "",
    ],
    queryFn: async () => {
      const slots = launchSlotsQ.data?.items ?? [];
      const responses = await Promise.all(
        slots.map((slot) =>
          apiGet<RoleLaunchBindingsResponse>(roleLaunchBindingsPath(slot.id)),
        ),
      );
      const items = responses.flatMap((response) => response.items);
      return { ok: true, items, count: items.length };
    },
    enabled: isSlotsTab && Boolean(agentID) && Boolean(launchSlotsQ.data),
  });
  const launchJobsQ = useQuery({
    queryKey: [
      "launch-jobs",
      agentID,
      roleBindingsQ.data?.items
        .map((binding) => binding.last_launch_job_id)
        .filter((id) => id > 0)
        .join(",") ?? "",
    ],
    queryFn: async () => {
      const ids = Array.from(
        new Set(
          (roleBindingsQ.data?.items ?? [])
            .map((binding) => binding.last_launch_job_id)
            .filter((id) => id > 0),
        ),
      );
      const responses = await Promise.all(
        ids.map((id) => apiGet<LaunchJobResponse>(launchJobPath(id))),
      );
      const items = responses.map((response) => response.job);
      return {
        ok: true,
        items,
        byID: new Map(items.map((job) => [job.id, job])),
      };
    },
    enabled: isSlotsTab && Boolean(agentID) && Boolean(roleBindingsQ.data),
  });
  const singBoxStateQ = useQuery({
    queryKey: ["agent-sing-box-state", agentID],
    queryFn: () => apiGet<AgentSingBoxStateResponse>(agentSingBoxStatePath(agentID)),
    enabled: needsAgentProxyData && Boolean(agentID),
  });
  const singBoxPlanQ = useQuery({
    queryKey: ["agent-sing-box-plan", agentID],
    queryFn: () => apiGet<AgentSingBoxPlanResponse>(agentSingBoxPlanPath(agentID)),
    enabled: needsAgentProxyData && Boolean(agentID),
  });
  const singBoxHistoryQ = useQuery({
    queryKey: ["agent-sing-box-history", agentID],
    queryFn: () => apiGet<AgentSingBoxHistoryResponse>(agentSingBoxHistoryPath(agentID)),
    enabled: isProxyTab && Boolean(agentID),
  });
  const singBoxVersionsQ = useQuery({
    queryKey: ["agent-sing-box-versions", agentID],
    queryFn: () => apiGet<AgentSingBoxVersionsResponse>(agentSingBoxVersionsPath(agentID)),
    enabled: isProxyTab && Boolean(agentID),
  });
  const agentCapacityQ = useQuery({
    queryKey: ["agent-launch-capacity", agentID],
    queryFn: () => apiGet<AgentLaunchCapacityResponse>(agentLaunchCapacityPath(agentID)),
    enabled: isProxyTab && Boolean(agentID),
  });
  const gameCapacitiesQ = useQuery({
    queryKey: ["game-client-capacities"],
    queryFn: () => apiGet<GameClientCapacitiesResponse>(gameClientCapacitiesPath),
    enabled: isSlotsTab && Boolean(agentID),
  });

  return (
    <div className="space-y-4">
      <Link to="/machines" search={{}} className="text-xs underline">
        ← 返回列表
      </Link>

      <QueryView query={q}>
        {(data) => (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="base">基础</TabsTrigger>
              <TabsTrigger value="auths">
                授权 ({data.auths.length})
              </TabsTrigger>
              <TabsTrigger value="caches">
                cache ({data.caches.length})
              </TabsTrigger>
              <TabsTrigger value="agents">
                在线 agent ({data.online_agents.length})
              </TabsTrigger>
              <TabsTrigger value="proxy">代理</TabsTrigger>
              <TabsTrigger value="slots">槽位</TabsTrigger>
              <TabsTrigger value="events">
                事件 ({data.events.length})
              </TabsTrigger>
              <TabsTrigger value="audit">
                审计 ({data.audit.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="base">
              <Card>
                <CardHeader>
                  <CardTitle>{data.machine.hostname}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-xs">
                    <dt className="text-[hsl(var(--muted-foreground))]">id</dt>
                    <dd className="font-mono">{data.machine.id}</dd>
                    <dt className="text-[hsl(var(--muted-foreground))]">
                      agent_id
                    </dt>
                    <dd className="font-mono">{data.machine.agent_id}</dd>
                    <dt className="text-[hsl(var(--muted-foreground))]">os</dt>
                    <dd>{data.machine.os ?? ""}</dd>
                    <dt className="text-[hsl(var(--muted-foreground))]">
                      version
                    </dt>
                    <dd>{data.machine.version ?? ""}</dd>
                    <dt className="text-[hsl(var(--muted-foreground))]">
                      created_at
                    </dt>
                    <dd className="font-mono">
                      {formatMs(data.machine.created_at)}
                    </dd>
                    <dt className="text-[hsl(var(--muted-foreground))]">
                      updated_at
                    </dt>
                    <dd className="font-mono">
                      {formatMs(data.machine.updated_at)}
                    </dd>
                  </dl>
                  <p className="mt-2 text-[10px] text-[hsl(var(--muted-foreground))]">
                    备注/标签按 auth 记录维度管理，见"授权"tab。
                  </p>
                </CardContent>
              </Card>
              <MarketCDPPanel machine={data.machine} canWrite={canWrite} />

              {canWrite && data.machine.agent_id ? (
                <>
                  <Card className="mt-4">
                    <CardHeader>
                      <CardTitle>agent 操作</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        <AgentCommandButton
                          agentID={data.machine.agent_id}
                          command="status"
                          label="探活"
                          actor={actor}
                        />
                        <AgentCommandButton
                          agentID={data.machine.agent_id}
                          command="rescan_caches"
                          label="刷新 cache"
                          actor={actor}
                          icon={
                            <RefreshCw
                              className="mr-1 h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                          }
                          onSuccess={() => {
                            refreshProxyQueries(queryClient, data.machine.agent_id);
                            void queryClient.invalidateQueries({
                              queryKey: ["machine-caches", machineId],
                            });
                          }}
                        />
                      </div>
                      <p className="mt-2 text-[10px] text-[hsl(var(--muted-foreground))]">
                        命令同步等 agent 返回 · 8s 超时
                      </p>
                    </CardContent>
                  </Card>
                  <div className="mt-4">
                    <LauncherHooksPanel
                      agentID={data.machine.agent_id}
                      actor={actor}
                      canWrite={canWrite}
                    />
                  </div>
                </>
              ) : null}
            </TabsContent>

            <TabsContent value="auths">
              <AuthsTable
                auths={data.auths}
                actor={actor}
                canWrite={canWrite}
                machineID={machineId}
              />
            </TabsContent>

            <TabsContent value="caches">
              <QueryView
                query={cachesQ}
                isEmpty={(d) => d.caches.length === 0}
              >
                {(c) => <CachesTable caches={c.caches} />}
              </QueryView>
            </TabsContent>

            <TabsContent value="agents">
              <ul className="space-y-1 text-xs">
                {data.online_agents.map((a) => (
                  <li key={a.agent_id} className="flex items-center gap-2">
                    <StatusBadge
                      variant={a.state === "online" ? "ok" : "warn"}
                    >
                      {a.state ?? "?"}
                    </StatusBadge>
                    <span className="font-mono">{a.agent_id}</span>
                    <span className="text-[hsl(var(--muted-foreground))]">
                      {a.hostname ?? ""}
                    </span>
                  </li>
                ))}
              </ul>
            </TabsContent>

            <TabsContent value="proxy">
              <ProxyManagementPanel
                agentID={dataAgentKey(data)}
                agentSingBox={agentSingBoxSnapshot(data)}
                profilesQuery={proxyProfilesQ}
                singBoxStateQuery={singBoxStateQ}
                singBoxPlanQuery={singBoxPlanQ}
                singBoxHistoryQuery={singBoxHistoryQ}
                singBoxVersionsQuery={singBoxVersionsQ}
                agentCapacityQuery={agentCapacityQ}
                canWrite={canWrite}
              />
            </TabsContent>

            <TabsContent value="slots">
              <LaunchSlotManagementPanel
                agentID={dataAgentKey(data)}
                profilesQuery={proxyProfilesQ}
                slotsQuery={launchSlotsQ}
                roleBindingsQuery={roleBindingsQ}
                launchJobsQuery={launchJobsQ}
                singBoxStateQuery={singBoxStateQ}
                singBoxPlanQuery={singBoxPlanQ}
                gameCapacitiesQuery={gameCapacitiesQ}
                actor={actor}
                canWrite={canWrite}
              />
            </TabsContent>

            <TabsContent value="events">
              <Table dense>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>类型</TableHead>
                    <TableHead>时间</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.events.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-mono">{e.id}</TableCell>
                      <TableCell className="font-mono">
                        {e.event_type}
                      </TableCell>
                      <TableCell className="font-mono">
                        {formatMs(e.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="audit">
              <div className="mb-2 text-xs text-[hsl(var(--muted-foreground))]">
                最近 {data.audit.length} 条 ·{" "}
                <Link
                  to="/audit"
                  search={{ domain: "machine" }}
                  className="underline"
                >
                  查看全部机器审计 →
                </Link>
              </div>
              <Table dense>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>动作</TableHead>
                    <TableHead>操作者</TableHead>
                    <TableHead>时间</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.audit.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-mono">{e.id}</TableCell>
                      <TableCell className="font-mono">{e.action}</TableCell>
                      <TableCell className="font-mono">{e.actor}</TableCell>
                      <TableCell className="font-mono">
                        {formatMs(e.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        )}
      </QueryView>
    </div>
  );
}

function dataAgentKey(data: MachineDetailResponse | undefined): string {
  return data?.machine.agent_id || data?.online_agents[0]?.agent_id || "";
}

type AgentSingBoxSnapshot = {
  enabled: boolean;
  config_hash: string;
  process_count: number;
  status: string;
  last_error: string;
};

function agentSingBoxSnapshot(
  data: MachineDetailResponse | undefined,
): AgentSingBoxSnapshot | null {
  const agentID = dataAgentKey(data);
  const agent = data?.online_agents.find((item) => item.agent_id === agentID);
  const raw = (agent as unknown as { sing_box?: unknown } | undefined)?.sing_box;
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const box = raw as Record<string, unknown>;
  const nestedConfig =
    box.config && typeof box.config === "object"
      ? (box.config as Record<string, unknown>)
      : {};
  return {
    enabled: box.enabled === true,
    config_hash: String(box.config_hash ?? nestedConfig.sha256 ?? ""),
    process_count: Number(box.process_count ?? 0),
    status: String(box.status ?? ""),
    last_error: String(box.last_error ?? box.error ?? ""),
  };
}

function apiErrorMessage(err: unknown): string {
  return err instanceof APIError
    ? err.message
    : err instanceof Error
      ? err.message
      : String(err);
}

function ProxyManagementPanel({
  agentID,
  agentSingBox,
  profilesQuery,
  singBoxStateQuery,
  singBoxPlanQuery,
  singBoxHistoryQuery,
  singBoxVersionsQuery,
  agentCapacityQuery,
  canWrite,
}: {
  agentID: string;
  agentSingBox: AgentSingBoxSnapshot | null;
  profilesQuery: UseQueryResult<ProxyProfilesResponse>;
  singBoxStateQuery: UseQueryResult<AgentSingBoxStateResponse>;
  singBoxPlanQuery: UseQueryResult<AgentSingBoxPlanResponse>;
  singBoxHistoryQuery: UseQueryResult<AgentSingBoxHistoryResponse>;
  singBoxVersionsQuery: UseQueryResult<AgentSingBoxVersionsResponse>;
  agentCapacityQuery: UseQueryResult<AgentLaunchCapacityResponse>;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [applying, setApplying] = useState(false);
  async function applyNow() {
    if (!agentID) {
      toast.error("当前机器没有 agent_id");
      return;
    }
    setApplying(true);
    try {
      const res = await apiPost<AgentSingBoxApplyResponse>(
        agentSingBoxApplyPath(agentID),
      );
      if (res.ok) {
        toast.success("已下发 sing-box 配置", {
          description:
            (res.assigned_count ?? 0) > 0
              ? `已自动绑定 ${res.assigned_count} 个槽位`
              : res.config_hash,
        });
      } else {
        toast.error("下发 sing-box 配置失败", {
          description: res.error || res.config_hash,
        });
      }
      refreshProxyQueries(queryClient, agentID, { delayed: true });
    } catch (err) {
      toast.error("下发 sing-box 配置失败", {
        description: apiErrorMessage(err),
      });
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-[hsl(var(--muted-foreground))]">
          agent <span className="font-mono">{agentID || "未绑定"}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={!canWrite || !agentID || applying}
            onClick={applyNow}
          >
            立即下发 sing-box
          </Button>
        </div>
      </div>
      {agentID ? (
        <QueryView query={agentCapacityQuery}>
          {(capacityData) => (
            <AgentLaunchCapacityPanel
              agentID={agentID}
              data={capacityData}
              canWrite={canWrite}
            />
          )}
        </QueryView>
      ) : null}
      <QueryView query={profilesQuery}>
        {(profilesData) => (
          <ProxyPoolShortcutPanel
            profiles={profilesData.items}
            count={profilesData.count}
          />
        )}
      </QueryView>
      {agentID ? (
        <QueryView query={singBoxStateQuery}>
          {(stateData) => (
            <SingBoxStatePanel
              agentID={agentID}
              state={stateData.state}
              plan={singBoxPlanQuery.data ?? null}
              agentSingBox={agentSingBox}
              history={singBoxHistoryQuery.data?.items ?? []}
              versions={singBoxVersionsQuery.data?.items ?? []}
              canWrite={canWrite}
            />
          )}
        </QueryView>
      ) : (
        <Card>
          <CardContent>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              当前机器没有可用 agent，无法下发 sing-box 配置。
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function LaunchSlotManagementPanel({
  agentID,
  profilesQuery,
  slotsQuery,
  roleBindingsQuery,
  launchJobsQuery,
  singBoxStateQuery,
  singBoxPlanQuery,
  gameCapacitiesQuery,
  actor,
  canWrite,
}: {
  agentID: string;
  profilesQuery: UseQueryResult<ProxyProfilesResponse>;
  slotsQuery: UseQueryResult<LaunchSlotsResponse>;
  roleBindingsQuery: UseQueryResult<RoleLaunchBindingsResponse>;
  launchJobsQuery: UseQueryResult<{
    ok: boolean;
    items: LaunchJobEntry[];
    byID: Map<number, LaunchJobEntry>;
  }>;
  singBoxStateQuery: UseQueryResult<AgentSingBoxStateResponse>;
  singBoxPlanQuery: UseQueryResult<AgentSingBoxPlanResponse>;
  gameCapacitiesQuery: UseQueryResult<GameClientCapacitiesResponse>;
  actor: string;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [autoAssigning, setAutoAssigning] = useState(false);
  async function autoAssign() {
    if (!agentID) {
      toast.error("当前机器没有 agent_id");
      return;
    }
    setAutoAssigning(true);
    try {
      const res = await apiPost<AgentSingBoxAutoAssignResponse>(
        agentSingBoxAutoAssignPath(agentID),
      );
      toast.success("已自动分配代理", {
        description:
          res.assigned_count > 0
            ? `${res.assigned_count} 个槽位已绑定代理`
            : "没有未绑定的槽位",
      });
      refreshProxyQueries(queryClient, agentID, { delayed: true });
    } catch (err) {
      toast.error("自动分配代理失败", {
        description: apiErrorMessage(err),
      });
    } finally {
      setAutoAssigning(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-[hsl(var(--muted-foreground))]">
          agent <span className="font-mono">{agentID || "未绑定"}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AgentCommandButton
            agentID={agentID}
            command="rescan_caches"
            label="扫描槽位"
            actor={actor}
            disabled={!canWrite || !agentID}
            icon={
              <RefreshCw
                className="mr-1 h-3.5 w-3.5"
                aria-hidden="true"
              />
            }
            onSuccess={() => {
              refreshProxyQueries(queryClient, agentID, { delayed: true });
              void queryClient.invalidateQueries({
                queryKey: ["machine-caches"],
              });
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canWrite || !agentID || autoAssigning}
            onClick={autoAssign}
          >
            {autoAssigning ? "分配中" : "自动分配槽位代理"}
          </Button>
        </div>
      </div>
      {agentID ? (
        <QueryView query={slotsQuery} isEmpty={(d) => d.items.length === 0}>
          {(slotsData) => (
            <QueryView query={profilesQuery}>
              {(profilesData) => (
                <LaunchSlotsProxyPanel
                  slots={slotsData.items}
                  profiles={profilesData.items}
                  agentID={agentID}
                  singBoxState={singBoxStateQuery.data?.state ?? null}
                  singBoxPlan={singBoxPlanQuery.data ?? null}
                  canWrite={canWrite}
                />
              )}
            </QueryView>
          )}
        </QueryView>
      ) : (
        <Card>
          <CardContent>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              当前机器没有可用 agent，无法读取槽位。
            </div>
          </CardContent>
        </Card>
      )}
      {agentID ? (
        <QueryView query={roleBindingsQuery}>
          {(bindingsData) => (
            <RoleLaunchBindingsPanel
              bindings={bindingsData.items}
              slots={slotsQuery.data?.items ?? []}
              launchJobs={launchJobsQuery.data?.byID ?? new Map()}
              canWrite={canWrite}
            />
          )}
        </QueryView>
      ) : null}
      {agentID ? (
        <QueryView query={slotsQuery} isEmpty={(d) => d.items.length === 0}>
          {(slotsData) => (
            <QueryView query={gameCapacitiesQuery}>
              {(gameCapacitiesData) => (
                <LaunchSlotCapacityPanel
                  slots={slotsData.items}
                  gameCapacities={gameCapacitiesData.items}
                  canWrite={canWrite}
                />
              )}
            </QueryView>
          )}
        </QueryView>
      ) : null}
    </div>
  );
}

function ProxyPoolShortcutPanel({
  profiles,
  count,
}: {
  profiles: ProxyProfileEntry[];
  count: number;
}) {
  const enabledCount = profiles.filter((profile) => profile.state === "enabled")
    .length;
  const failedProbeCount = profiles.filter(
    (profile) => profile.last_probe_at > 0 && !profile.last_probe_ok,
  ).length;
  const enabledHint = proxyPoolEnabledHint(enabledCount);
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>上游代理池</CardTitle>
            <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
              代理节点是 daemon 全局资源，当前机器页只负责槽位绑定和下发。
            </p>
          </div>
          <Button asChild size="sm" variant="outline">
            <Link to="/proxy-profiles" search={{} as never}>
              管理代理池
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {enabledHint ? (
          <div className="mb-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))] px-3 py-2 text-xs text-[hsl(var(--muted-foreground))]">
            {enabledHint}
          </div>
        ) : null}
        <div className="grid gap-3 text-xs md:grid-cols-3">
          <div>
            <dt className="text-[hsl(var(--muted-foreground))]">代理节点</dt>
            <dd className="font-mono text-lg">{count}</dd>
          </div>
          <div>
            <dt className="text-[hsl(var(--muted-foreground))]">启用节点</dt>
            <dd className="font-mono text-lg">{enabledCount}</dd>
          </div>
          <div>
            <dt className="text-[hsl(var(--muted-foreground))]">探测失败</dt>
            <dd className="font-mono text-lg">{failedProbeCount}</dd>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AgentLaunchCapacityPanel({
  agentID,
  data,
  canWrite,
}: {
  agentID: string;
  data: AgentLaunchCapacityResponse;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [manualMax, setManualMax] = useState(
    data.capacity?.manual_max_instances === undefined
      ? ""
      : String(data.capacity.manual_max_instances),
  );
  const [enforceMode, setEnforceMode] = useState(
    data.capacity?.enforce_mode || "enforce",
  );
  const [note, setNote] = useState(data.capacity?.note || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setManualMax(
      data.capacity?.manual_max_instances === undefined
        ? ""
        : String(data.capacity.manual_max_instances),
    );
    setEnforceMode(data.capacity?.enforce_mode || "enforce");
    setNote(data.capacity?.note || "");
  }, [
    data.capacity?.agent_id,
    data.capacity?.enforce_mode,
    data.capacity?.manual_max_instances,
    data.capacity?.note,
  ]);

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    let manual: number | null;
    try {
      manual = parseOptionalCapacityValue(manualMax);
    } catch (err) {
      toast.error("主机容量输入无效", { description: apiErrorMessage(err) });
      return;
    }
    setSaving(true);
    try {
      await apiPost<AgentLaunchCapacityResponse>(
        agentLaunchCapacityPath(agentID),
        {
          manual_max_instances: manual,
          enforce_mode: enforceMode,
          note,
        },
      );
      toast.success("已保存 agent 总容量", { description: agentID });
      void queryClient.invalidateQueries({
        queryKey: ["agent-launch-capacity", agentID],
      });
    } catch (err) {
      toast.error("保存 agent 总容量失败", {
        description: apiErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  }

  const check = data.check;
  const variant = !check.ok
    ? "danger"
    : check.effective_source === "unconfigured"
      ? "neutral"
      : check.enforce_mode === "warn"
        ? "warn"
        : "ok";

  return (
    <Card>
      <CardHeader>
        <CardTitle>agent 总容量</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
          <div className="grid gap-3 text-xs md:grid-cols-4">
            <div>
              <dt className="text-[hsl(var(--muted-foreground))]">当前进程</dt>
              <dd className="font-mono text-lg">{check.current_instance_count}</dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--muted-foreground))]">上限</dt>
              <dd className="font-mono text-lg">
                {capacityLabel(data.capacity?.manual_max_instances)}
              </dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--muted-foreground))]">模式</dt>
              <dd>
                <StatusBadge variant={variant}>{check.enforce_mode}</StatusBadge>
              </dd>
            </div>
            <div>
              <dt className="text-[hsl(var(--muted-foreground))]">来源</dt>
              <dd className="font-mono text-[11px]">{check.effective_source}</dd>
            </div>
          </div>
          <form className="grid gap-2 md:grid-cols-[90px_110px_1fr_auto]" onSubmit={save}>
            <Input
              value={manualMax}
              onChange={(e) => setManualMax(e.target.value)}
              disabled={!canWrite || saving}
              className="h-8 font-mono text-xs"
              placeholder="空=不限制"
            />
            <Select
              value={enforceMode}
              onValueChange={setEnforceMode}
              disabled={!canWrite || saving}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="enforce">enforce</SelectItem>
                <SelectItem value="warn">warn</SelectItem>
                <SelectItem value="disabled">disabled</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={!canWrite || saving}
              className="h-8 text-xs"
              placeholder="note"
            />
            <Button type="submit" size="sm" disabled={!canWrite || saving}>
              保存
            </Button>
          </form>
        </div>
        {check.reason ? (
          <div className="mt-2 text-xs text-[hsl(var(--destructive))]">
            {check.reason}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function shortHash(value: string | undefined): string {
  const text = String(value ?? "").trim();
  return text ? text.slice(0, 12) : "未记录";
}

function parseJSONRecord(value: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(value) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // ignore invalid audit payload json
  }
  return {};
}

function recordFromUnknown(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function singBoxApplyPayload(payload: Record<string, unknown>) {
  const response = recordFromUnknown(payload.response);
  const result = recordFromUnknown(response.result);
  return Object.keys(result).length > 0 ? result : response;
}

function statusFromRecord(record: Record<string, unknown>, fallback = "") {
  return String(record.status ?? record.error ?? fallback);
}

function statePendingRestart(state: AgentSingBoxStateResponse["state"]) {
  if (!state) return false;
  if (state.pending_restart === true) return true;
  return state.status_json.includes("pending_restart");
}

function singBoxSyncVariant(
  state: AgentSingBoxStateResponse["state"],
  agentSingBox: AgentSingBoxSnapshot | null,
) {
  if (!state) return "neutral" as const;
  if (state.last_error) return "danger" as const;
  if (statePendingRestart(state)) return "warn" as const;
  if (state.desired_config_hash && !state.applied_config_hash) {
    return "warn" as const;
  }
  if (
    state.desired_config_hash &&
    state.applied_config_hash &&
    state.desired_config_hash !== state.applied_config_hash
  ) {
    return "drift" as const;
  }
  if (
    agentSingBox?.config_hash &&
    state.desired_config_hash &&
    agentSingBox.config_hash !== state.desired_config_hash
  ) {
    return "drift" as const;
  }
  return state.last_apply_ok ? "ok" as const : "neutral" as const;
}

function singBoxStatusText(
  state: AgentSingBoxStateResponse["state"],
  agentSingBox: AgentSingBoxSnapshot | null,
) {
  if (!state) return "尚未下发";
  if (state.last_error) return "最近失败";
  if (statePendingRestart(state)) return "等待重启";
  if (state.desired_config_hash && !state.applied_config_hash) return "等待应用";
  if (
    state.desired_config_hash &&
    state.applied_config_hash &&
    state.desired_config_hash !== state.applied_config_hash
  ) {
    return "daemon/agent 不一致";
  }
  if (
    agentSingBox?.config_hash &&
    state.desired_config_hash &&
    agentSingBox.config_hash !== state.desired_config_hash
  ) {
    return "上报 hash 不一致";
  }
  return state.last_apply_ok ? "已同步" : "无应用结果";
}

function singBoxPlanVariant(plan: AgentSingBoxPlanResponse | null) {
  if (!plan) return "neutral" as const;
  if (plan.ready) return "ok" as const;
  if (plan.blockers.length > 0) return "danger" as const;
  if (plan.desired?.plan_kind === "cleanup") return "warn" as const;
  return "warn" as const;
}

function singBoxPlanText(plan: AgentSingBoxPlanResponse | null) {
  if (!plan) return "未读取";
  if (plan.ready) return "可下发";
  if (plan.desired?.plan_kind === "cleanup") return "清理配置";
  if (plan.blockers.length > 0) return "不可下发";
  return "需确认";
}

export function singBoxSlotBlockedReasonText(reason: string | undefined) {
  switch (reason) {
    case "proxy_profile_not_enabled":
      return "代理未启用";
    case "proxy_profile_not_found":
      return "代理不存在";
    case "proxy_profile_unassigned":
      return "未分配代理";
    default:
      return reason || "";
  }
}

export type SingBoxAppliedGate = {
  variant: StatusVariant;
  label: string;
  detail: string;
};

export function singBoxAppliedGateStatus(
  state: AgentSingBoxStateResponse["state"],
  plan: AgentSingBoxPlanResponse | null,
): SingBoxAppliedGate {
  const expectedHash = String(plan?.desired?.config_hash ?? "").trim();
  const stateDesiredHash = String(state?.desired_config_hash ?? "").trim();
  const stateAppliedHash = String(state?.applied_config_hash ?? "").trim();
  const agentConfigHash = String(plan?.agent_sing_box_config_hash ?? "").trim();
  const agentConfigDrift = String(plan?.agent_sing_box_config_drift ?? "").trim();
  if (!plan) {
    return { variant: "neutral", label: "未读取", detail: "等待 plan" };
  }
  if (plan.managed_slot_count <= 0) {
    if (plan.enabled_slot_count > 0) {
      return {
        variant: plan.blockers.length > 0 ? "danger" : "warn",
        label: "未托管",
        detail: plan.blockers[0] || "enabled 槽位未绑定代理",
      };
    }
    return { variant: "neutral", label: "不适用", detail: "无托管槽位" };
  }
  if (!expectedHash) {
    return {
      variant: plan.blockers.length > 0 || plan.config_build_error ? "danger" : "warn",
      label: "无期望 hash",
      detail: plan.config_build_error || plan.blockers[0] || "未生成",
    };
  }
  if (!state) {
    return { variant: "warn", label: "未应用", detail: `want ${shortHash(expectedHash)}` };
  }
  if (state.last_error) {
    return { variant: "danger", label: "apply 失败", detail: state.last_error };
  }
  if (statePendingRestart(state)) {
    return {
      variant: "warn",
      label: "待重启",
      detail: `重启 sing-box 后生效 ${shortHash(expectedHash)}`,
    };
  }
  if (!state.last_apply_ok) {
    return { variant: "warn", label: "未成功", detail: `want ${shortHash(expectedHash)}` };
  }
  if (stateDesiredHash !== expectedHash) {
    return {
      variant: "drift",
      label: "desired 过期",
      detail: `state ${shortHash(stateDesiredHash)} / want ${shortHash(expectedHash)}`,
    };
  }
  if (stateAppliedHash !== expectedHash) {
    return {
      variant: "drift",
      label: "applied 过期",
      detail: `applied ${shortHash(stateAppliedHash)} / want ${shortHash(expectedHash)}`,
    };
  }
  if (agentConfigDrift === "drifted") {
    return {
      variant: "drift",
      label: "agent hash 漂移",
      detail: `agent ${shortHash(agentConfigHash)} / want ${shortHash(expectedHash)}`,
    };
  }
  return {
    variant: "ok",
    label: "已应用",
    detail: `applied ${shortHash(expectedHash)}`,
  };
}

export function singBoxInstallGateStatus(
  plan: AgentSingBoxPlanResponse | null,
): SingBoxAppliedGate {
  const exeExists = plan?.agent_sing_box_exe_exists ?? null;
  const agentDownloadConfigured = Boolean(
    plan?.agent_sing_box_download_configured,
  );
  const daemonPackageConfigured = Boolean(
    plan?.daemon_sing_box_package_configured,
  );
  if (exeExists == null) {
    return {
      variant: "neutral",
      label: plan?.agent_online === false ? "离线" : "未上报",
      detail: "",
    };
  }
  if (exeExists) {
    return { variant: "ok", label: "exe 已存在", detail: "" };
  }
  if (daemonPackageConfigured) {
    return {
      variant: "ok",
      label: "daemon 包源",
      detail: plan?.agent_sing_box_last_error || "daemon package ready",
    };
  }
  if (agentDownloadConfigured) {
    return {
      variant: "ok",
      label: "agent 下载源",
      detail: plan?.agent_sing_box_last_error || "agent download ready",
    };
  }
  return {
    variant: "danger",
    label: "缺少下载源",
    detail: plan?.agent_sing_box_last_error || "",
  };
}

export function launchSlotProxyProfileOptions(
  profiles: ProxyProfileEntry[],
  currentProxyProfileID: number,
) {
  return profiles.filter(
    (profile) =>
      profile.state === "enabled" || profile.id === currentProxyProfileID,
  );
}

function launchSlotProxyBindingValue(slot: LaunchSlotEntry) {
  if (slot.proxy_profile_id > 0) {
    return `profile:${slot.proxy_profile_id}`;
  }
  if (slot.proxy_binding_mode === "none") {
    return "none";
  }
  return "auto";
}

export function proxyPoolEnabledHint(enabledCount: number) {
  if (enabledCount > 0) return "";
  return "当前没有启用的上游代理节点，未绑定槽位不会自动分配。请在代理池页导入或启用节点后再 apply。";
}

function SingBoxStatePanel({
  agentID,
  state,
  plan,
  agentSingBox,
  history,
  versions,
  canWrite,
}: {
  agentID: string;
  state: AgentSingBoxStateResponse["state"];
  plan: AgentSingBoxPlanResponse | null;
  agentSingBox: AgentSingBoxSnapshot | null;
  history: AgentSingBoxHistoryResponse["items"];
  versions: AgentSingBoxVersionsResponse["items"];
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [rollingBackID, setRollingBackID] = useState<number | null>(null);
  const statusVariant = singBoxSyncVariant(state, agentSingBox);
  const statusText = singBoxStatusText(state, agentSingBox);
  const planVariant = singBoxPlanVariant(plan);
  const planText = singBoxPlanText(plan);
  const appliedGate = singBoxAppliedGateStatus(state, plan);
  const agentSingBoxEnabled =
    plan?.agent_sing_box_enabled ?? agentSingBox?.enabled ?? null;
  const agentSingBoxReady =
    plan?.agent_sing_box_ready ??
    (agentSingBox ? agentSingBox.status === "running" : null);
  const agentSingBoxConfigLabel =
    agentSingBoxEnabled == null
      ? plan?.agent_online === false
        ? "离线"
        : "未上报"
      : agentSingBoxEnabled
        ? agentSingBoxReady === false
          ? "enabled / not ready"
          : "enabled"
        : "disabled";
  const agentSingBoxConfigVariant =
    agentSingBoxEnabled == null
      ? ("neutral" as const)
      : agentSingBoxEnabled
        ? agentSingBoxReady === false
          ? ("warn" as const)
          : ("ok" as const)
        : ("danger" as const);
  const singBoxInstallGate = singBoxInstallGateStatus(plan);
  const lastStatus = state?.status_json
    ? (() => {
        try {
          const parsed = JSON.parse(state.status_json) as Record<string, unknown>;
          return String(parsed.status ?? parsed.error ?? "");
        } catch {
          return "";
        }
      })()
    : "";
  const lastApply = state?.status_json
    ? singBoxApplyPayload(parseJSONRecord(state.status_json))
    : {};
  const lastCheck = recordFromUnknown(lastApply.check);
  const lastRestart = recordFromUnknown(lastApply.restart_result);
  const lastRollback = recordFromUnknown(lastApply.rollback);
  const lastInstall = recordFromUnknown(lastApply.install);
  const healthRows = [
    {
      label: "配置检查",
      value: statusFromRecord(lastCheck, lastApply.check ? "ok" : "未记录"),
      variant: lastCheck.error ? "danger" : lastApply.check ? "ok" : "neutral",
    },
    {
      label: "重启",
      value: statusFromRecord(lastRestart, lastApply.restart ? "未记录" : "未请求"),
      variant: lastRestart.error ? "danger" : lastRestart.status ? "ok" : "neutral",
    },
    {
      label: "回滚",
      value:
        String(lastRollback.status ?? "") ||
        (lastRollback.attempted ? (lastRollback.ok ? "rolled_back" : "failed") : "未触发"),
      variant: lastRollback.ok ? "warn" : lastRollback.error ? "danger" : "neutral",
    },
    {
      label: "安装",
      value: statusFromRecord(lastInstall, lastApply.install ? "ok" : "未记录"),
      variant: lastInstall.error ? "danger" : lastApply.install ? "ok" : "neutral",
    },
  ] as const;

  async function rollback(versionID: number) {
    setRollingBackID(versionID);
    try {
      const result = await apiPostForm<AgentSingBoxRollbackResponse>(
        agentSingBoxRollbackPath(agentID),
        { version_id: versionID },
      );
      if (result.ok) {
        toast.success("已回滚并下发 sing-box 配置", {
          description: shortHash(result.config_hash),
        });
      } else {
        toast.error("回滚 sing-box 配置失败", {
          description: result.error || shortHash(result.config_hash),
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["machine"] });
      void queryClient.invalidateQueries({ queryKey: ["agent-sing-box-state", agentID] });
      void queryClient.invalidateQueries({ queryKey: ["agent-sing-box-history", agentID] });
      void queryClient.invalidateQueries({ queryKey: ["agent-sing-box-versions", agentID] });
    } catch (err) {
      toast.error("回滚 sing-box 配置失败", {
        description: apiErrorMessage(err),
      });
    } finally {
      setRollingBackID(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>sing-box 下发状态</CardTitle>
          <StatusBadge variant={statusVariant}>{statusText}</StatusBadge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 rounded border border-[hsl(var(--border))] p-3 text-xs">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="font-medium">daemon 分发计划</div>
            <StatusBadge variant={planVariant}>{planText}</StatusBadge>
          </div>
          <dl className="grid gap-2 md:grid-cols-[120px_1fr_120px_1fr]">
            <dt className="text-[hsl(var(--muted-foreground))]">代理池</dt>
            <dd>
              {plan
                ? `${plan.enabled_proxy_profile_count}/${plan.proxy_profile_count} enabled`
                : "未读取"}
            </dd>
            <dt className="text-[hsl(var(--muted-foreground))]">槽位</dt>
            <dd>
              {plan
                ? `${plan.managed_slot_count}/${plan.enabled_slot_count} 托管`
                : "未读取"}
            </dd>
            <dt className="text-[hsl(var(--muted-foreground))]">计划类型</dt>
            <dd>{plan?.desired?.plan_kind ?? "未生成"}</dd>
            <dt className="text-[hsl(var(--muted-foreground))]">立即下发</dt>
            <dd>
              {plan
                ? (plan.manual_apply_auto_assign_count ?? 0) > 0
                  ? `会自动绑定 ${plan.manual_apply_auto_assign_count} 个槽位`
                  : plan.manual_apply_plan_kind || "按当前绑定"
                : "未读取"}
            </dd>
            <dt className="text-[hsl(var(--muted-foreground))]">agent 配置</dt>
            <dd>
              <StatusBadge variant={agentSingBoxConfigVariant}>
                {agentSingBoxConfigLabel}
              </StatusBadge>
            </dd>
            <dt className="text-[hsl(var(--muted-foreground))]">安装就绪</dt>
            <dd>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge variant={singBoxInstallGate.variant}>
                  {singBoxInstallGate.label}
                </StatusBadge>
                {singBoxInstallGate.detail ? (
                  <span className="max-w-96 truncate text-[10px] text-[hsl(var(--muted-foreground))]">
                    {singBoxInstallGate.detail}
                  </span>
                ) : null}
              </div>
            </dd>
            <dt className="text-[hsl(var(--muted-foreground))]">计划 hash</dt>
            <dd className="font-mono">{shortHash(plan?.desired?.config_hash)}</dd>
            <dt className="text-[hsl(var(--muted-foreground))]">启动门禁</dt>
            <dd>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge variant={appliedGate.variant}>
                  {appliedGate.label}
                </StatusBadge>
                <span className="max-w-96 truncate font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                  {appliedGate.detail}
                </span>
              </div>
            </dd>
          </dl>
          {plan?.blockers.length ? (
            <div className="mt-3 rounded border border-[hsl(var(--border))] p-2 text-[hsl(var(--danger))]">
              {plan.blockers.slice(0, 3).join("；")}
            </div>
          ) : plan?.warnings.length ? (
            <div className="mt-3 rounded border border-[hsl(var(--border))] p-2 text-[hsl(var(--warn))]">
              {plan.warnings.slice(0, 3).join("；")}
            </div>
          ) : null}
          {plan?.slots.length ? (
            <div className="mt-3 overflow-x-auto">
              <Table dense>
                <TableHeader>
                  <TableRow>
                    <TableHead>槽位</TableHead>
                    <TableHead>代理</TableHead>
                    <TableHead>outbound</TableHead>
                    <TableHead>规则</TableHead>
                    <TableHead>出口</TableHead>
                    <TableHead>诊断</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plan.slots.map((slot) => (
                    <TableRow key={slot.id}>
                      <TableCell>
                        <div className="font-mono text-[10px]">
                          {slot.slot_key}
                        </div>
                        <div className="max-w-56 truncate text-[10px] text-[hsl(var(--muted-foreground))]">
                          {slot.resource_root}
                        </div>
                      </TableCell>
                      <TableCell>
                        {slot.proxy_profile_id > 0 ? (
                          <div className="space-y-1">
                            <div className="font-mono text-[10px]">
                              {slot.proxy_profile_key || `#${slot.proxy_profile_id}`}
                            </div>
                            <StatusBadge
                              variant={
                                slot.proxy_profile_state === "enabled"
                                  ? "ok"
                                  : "neutral"
                              }
                            >
                              {slot.proxy_profile_state || "missing"}
                            </StatusBadge>
                          </div>
                        ) : slot.unassigned ? (
                          <StatusBadge variant="warn">未分配</StatusBadge>
                        ) : (
                          <StatusBadge variant="neutral">未绑定</StatusBadge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-[10px]">
                        {slot.effective_proxy_outbound_tag || ""}
                      </TableCell>
                      <TableCell>
                        {slot.managed ? (
                          <StatusBadge
                            variant={slot.sing_box_rule_active ? "ok" : "warn"}
                          >
                            {slot.sing_box_rule_active ? "active" : "inactive"}
                          </StatusBadge>
                        ) : (
                          <StatusBadge variant="neutral">not managed</StatusBadge>
                        )}
                        {slot.sing_box_rule_reason ? (
                          <div className="mt-1 max-w-56 truncate text-[10px] text-[hsl(var(--muted-foreground))]">
                            {slot.sing_box_rule_reason}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-[10px]">
                          {slot.last_exit_ip || ""}
                        </div>
                        {slot.probe_exit_ip ? (
                          <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
                            probe {slot.probe_exit_ip}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {slot.blocked_reason ? (
                            <>
                              <StatusBadge variant="danger">
                                {singBoxSlotBlockedReasonText(slot.blocked_reason)}
                              </StatusBadge>
                              <div className="max-w-56 truncate font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                                {slot.blocked_reason}
                              </div>
                            </>
                          ) : slot.managed && !slot.last_exit_ip ? (
                            <StatusBadge variant="warn">未探测</StatusBadge>
                          ) : (
                            <StatusBadge variant={slot.managed ? "ok" : "neutral"}>
                              {slot.managed ? "ok" : slot.state}
                            </StatusBadge>
                          )}
                          {slot.managed ? (
                            <>
                              <StatusBadge variant={appliedGate.variant}>
                                {appliedGate.label}
                              </StatusBadge>
                              <div className="max-w-56 truncate font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                                {appliedGate.detail}
                              </div>
                            </>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </div>
        <dl className="grid gap-2 text-xs md:grid-cols-[140px_1fr_140px_1fr]">
          <dt className="text-[hsl(var(--muted-foreground))]">daemon 期望</dt>
          <dd className="font-mono">{shortHash(state?.desired_config_hash)}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">最近应用</dt>
          <dd className="font-mono">{shortHash(state?.applied_config_hash)}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">agent 上报</dt>
          <dd className="font-mono">{shortHash(agentSingBox?.config_hash)}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">进程数</dt>
          <dd>{agentSingBox ? agentSingBox.process_count : "未上报"}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">desired generation</dt>
          <dd className="font-mono">{state?.desired_generation || "未记录"}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">applied at</dt>
          <dd className="font-mono">
            {state?.applied_at ? formatMs(state.applied_at) : "未记录"}
          </dd>
        </dl>
        {state?.last_error || agentSingBox?.last_error ? (
          <div className="mt-3 rounded border border-[hsl(var(--border))] p-2 text-xs text-[hsl(var(--danger))]">
            {state?.last_error || agentSingBox?.last_error}
          </div>
        ) : lastStatus ? (
          <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
            最近结果：<span className="font-mono">{lastStatus}</span>
          </div>
        ) : null}
        <div className="mt-4 grid gap-2 text-xs md:grid-cols-4">
          {healthRows.map((row) => (
            <div
              key={row.label}
              className="rounded border border-[hsl(var(--border))] p-2"
            >
              <div className="mb-1 text-[hsl(var(--muted-foreground))]">
                {row.label}
              </div>
              <StatusBadge variant={row.variant}>{row.value}</StatusBadge>
            </div>
          ))}
        </div>
        {history.length > 0 ? (
          <div className="mt-4">
            <div className="mb-2 text-xs font-medium">最近下发历史</div>
            <Table dense>
              <TableHeader>
                <TableRow>
                  <TableHead>时间</TableHead>
                  <TableHead>动作</TableHead>
                  <TableHead>配置</TableHead>
                  <TableHead>结果</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.slice(0, 6).map((item) => {
                  const payload = parseJSONRecord(item.payload_json);
                  const apply = singBoxApplyPayload(payload);
                  const hash = String(payload.config_hash ?? "");
                  const generation = String(payload.generation ?? "");
                  const ok = payload.ok as boolean | undefined;
                  const error = String(payload.error ?? "");
                  const checkStatus = statusFromRecord(recordFromUnknown(apply.check));
                  const restartStatus = statusFromRecord(recordFromUnknown(apply.restart_result));
                  const rollback = recordFromUnknown(apply.rollback);
                  const rollbackStatus =
                    String(rollback.status ?? "") ||
                    (rollback.attempted
                      ? rollback.ok
                        ? "rolled_back"
                        : "failed"
                      : "");
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-[10px]">
                        {formatMs(item.created_at)}
                      </TableCell>
                      <TableCell className="font-mono text-[10px]">
                        {item.action}
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-[10px]">
                          {shortHash(hash)}
                        </div>
                        {generation ? (
                          <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                            gen {generation}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {item.action.endsWith("requested") ? (
                          <StatusBadge variant="neutral">requested</StatusBadge>
                        ) : ok === false || error ? (
                          <div className="flex flex-col gap-1">
                            <StatusBadge variant="danger">failed</StatusBadge>
                            {error ? (
                              <span className="max-w-80 truncate text-[10px] text-[hsl(var(--destructive))]">
                                {error}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <StatusBadge variant="ok">ok</StatusBadge>
                        )}
                        {checkStatus || restartStatus || rollbackStatus ? (
                          <div className="mt-1 flex max-w-80 flex-wrap gap-1 font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                            {checkStatus ? <span>check:{checkStatus}</span> : null}
                            {restartStatus ? <span>restart:{restartStatus}</span> : null}
                            {rollbackStatus ? <span>rollback:{rollbackStatus}</span> : null}
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : null}
        {versions.length > 0 ? (
          <div className="mt-4">
            <div className="mb-2 text-xs font-medium">配置版本</div>
            <Table dense>
              <TableHeader>
                <TableRow>
                  <TableHead>时间</TableHead>
                  <TableHead>来源</TableHead>
                  <TableHead>配置</TableHead>
                  <TableHead>规模</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.slice(0, 6).map((version) => (
                  <TableRow key={version.id}>
                    <TableCell className="font-mono text-[10px]">
                      {formatMs(version.created_at)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        variant={version.source === "rollback" ? "warn" : "neutral"}
                      >
                        {version.source || "generated"}
                      </StatusBadge>
                      {version.note ? (
                        <div className="mt-1 max-w-48 truncate text-[10px] text-[hsl(var(--muted-foreground))]">
                          {version.note}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <div className="font-mono text-[10px]">
                        {shortHash(version.config_hash)}
                      </div>
                      <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                        gen {version.generation}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-[10px]">
                      {version.slot_count} slots / {version.outbound_count} out
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!canWrite || rollingBackID !== null}
                        onClick={() => void rollback(version.id)}
                      >
                        {rollingBackID === version.id ? "回滚中" : "回滚"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function LaunchSlotsProxyPanel({
  slots,
  profiles,
  agentID,
  singBoxState,
  singBoxPlan,
  canWrite,
}: {
  slots: LaunchSlotEntry[];
  profiles: ProxyProfileEntry[];
  agentID: string;
  singBoxState: AgentSingBoxStateResponse["state"];
  singBoxPlan: AgentSingBoxPlanResponse | null;
  canWrite: boolean;
}) {
  const appliedGate = singBoxAppliedGateStatus(singBoxState, singBoxPlan);
  return (
    <Card>
      <CardHeader>
        <CardTitle>槽位代理绑定</CardTitle>
      </CardHeader>
      <CardContent>
        <Table dense>
          <TableHeader>
            <TableRow>
              <TableHead>槽位</TableHead>
              <TableHead>资源目录</TableHead>
              <TableHead>代理节点</TableHead>
              <TableHead>outbound</TableHead>
              <TableHead>规则</TableHead>
              <TableHead>出口检测</TableHead>
              <TableHead>状态</TableHead>
              <TableHead>保存</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {slots.map((slot) => (
              <LaunchSlotProxyRow
                key={slot.id}
                slot={slot}
                profiles={profiles}
                agentID={agentID}
                appliedGate={appliedGate}
                canWrite={canWrite}
              />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function RoleLaunchBindingsPanel({
  bindings,
  slots,
  launchJobs,
  canWrite,
}: {
  bindings: RoleLaunchBindingEntry[];
  slots: LaunchSlotEntry[];
  launchJobs: Map<number, LaunchJobEntry>;
  canWrite: boolean;
}) {
  const slotByID = new Map(slots.map((slot) => [slot.id, slot]));
  return (
    <Card>
      <CardHeader>
        <CardTitle>角色槽位绑定</CardTitle>
      </CardHeader>
      <CardContent>
        {bindings.length === 0 ? (
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            当前 agent 没有角色启动绑定。
          </div>
        ) : (
          <Table dense>
            <TableHeader>
              <TableRow>
                <TableHead>角色</TableHead>
                <TableHead>目标</TableHead>
                <TableHead>槽位</TableHead>
                <TableHead>最近实例</TableHead>
                <TableHead>启动任务</TableHead>
                <TableHead>状态</TableHead>
                <TableHead>操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bindings.map((binding) => (
                <RoleLaunchBindingRow
                  key={binding.id}
                  binding={binding}
                  slot={slotByID.get(binding.slot_id)}
                  launchJob={launchJobs.get(binding.last_launch_job_id)}
                  canWrite={canWrite}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function RoleLaunchBindingRow({
  binding,
  slot,
  launchJob,
  canWrite,
}: {
  binding: RoleLaunchBindingEntry;
  slot: LaunchSlotEntry | undefined;
  launchJob: LaunchJobEntry | undefined;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [stopping, setStopping] = useState(false);
  const [restarting, setRestarting] = useState(false);
  function invalidateRoleBindingViews() {
    void queryClient.invalidateQueries({ queryKey: ["role-launch-bindings"] });
    void queryClient.invalidateQueries({ queryKey: ["launch-jobs"] });
    void queryClient.invalidateQueries({ queryKey: ["launch-slots"] });
    void queryClient.invalidateQueries({ queryKey: ["machine"] });
  }
  async function stop() {
    setStopping(true);
    try {
      await apiPostForm<RoleLaunchBindingActionResponse>(
        roleLaunchBindingStopPath(binding.id),
        {},
      );
      toast.success("已停止绑定实例", {
        description: binding.role_name || binding.expected_role_name || binding.game_account,
      });
      invalidateRoleBindingViews();
    } catch (err) {
      toast.error("停止绑定实例失败", { description: apiErrorMessage(err) });
    } finally {
      setStopping(false);
    }
  }
  async function restart() {
    setRestarting(true);
    try {
      await apiPostForm<RoleLaunchBindingActionResponse>(
        roleLaunchBindingRestartPath(binding.id),
        {},
      );
      toast.success("已重启绑定实例", {
        description: binding.role_name || binding.expected_role_name || binding.game_account,
      });
      invalidateRoleBindingViews();
    } catch (err) {
      toast.error("重启绑定实例失败", { description: apiErrorMessage(err) });
    } finally {
      setRestarting(false);
    }
  }
  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">
          {binding.role_name || binding.expected_role_name || "未绑定角色"}
        </div>
        <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
          {binding.game_account || `account:${binding.account_id}`}
        </div>
      </TableCell>
      <TableCell>
        <div className="font-mono text-xs">{binding.game_id}</div>
        <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
          {binding.server_id}
        </div>
      </TableCell>
      <TableCell>
        <div className="font-mono text-xs">{slot?.slot_key || binding.slot_id}</div>
        <div className="max-w-72 truncate text-[10px] text-[hsl(var(--muted-foreground))]">
          {slot?.resource_root || binding.last_resource_root}
        </div>
      </TableCell>
      <TableCell>
        <div className="font-mono text-[10px]">
          {binding.last_instance_id || "未上线"}
        </div>
        <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
          pid {binding.last_process_id || "-"}
        </div>
      </TableCell>
      <TableCell>
        {binding.last_launch_job_id > 0 ? (
          <>
            <div className="font-mono text-[10px]">
              #{binding.last_launch_job_id} {launchJob?.status ?? "读取中"}
            </div>
            <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
              {launchJob?.target_server_id || binding.server_id}
            </div>
            {launchJob?.error ? (
              <div className="max-w-48 truncate text-[10px] text-[hsl(var(--destructive))]">
                {launchJob.error}
              </div>
            ) : null}
          </>
        ) : (
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
            无任务
          </span>
        )}
      </TableCell>
      <TableCell>
        <StatusBadge
          variant={binding.status === "bound" ? "ok" : binding.status === "conflict" ? "danger" : "neutral"}
        >
          {binding.status}
        </StatusBadge>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={!canWrite || restarting || !binding.last_instance_id}
            onClick={() => void restart()}
          >
            {restarting ? "重启中" : "重启"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canWrite || stopping || !binding.last_instance_id}
            onClick={() => void stop()}
          >
            {stopping ? "停止中" : "停止"}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function LaunchSlotProxyRow({
  slot,
  profiles,
  agentID,
  appliedGate,
  canWrite,
}: {
  slot: LaunchSlotEntry;
  profiles: ProxyProfileEntry[];
  agentID: string;
  appliedGate: SingBoxAppliedGate;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [proxyBindingValue, setProxyBindingValue] = useState(
    launchSlotProxyBindingValue(slot),
  );
  const [outboundTag, setOutboundTag] = useState(slot.proxy_outbound_tag || "");
  const [exitIPCheckMode, setExitIPCheckMode] = useState(
    slot.exit_ip_check_mode || "warn",
  );
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [probing, setProbing] = useState(false);

  useEffect(() => {
    setProxyBindingValue(launchSlotProxyBindingValue(slot));
    setOutboundTag(slot.proxy_outbound_tag || "");
    setExitIPCheckMode(slot.exit_ip_check_mode || "warn");
  }, [
    slot.id,
    slot.proxy_profile_id,
    slot.proxy_outbound_tag,
    slot.exit_ip_check_mode,
  ]);

  async function save() {
    setSaving(true);
    try {
      const normalizedProxyID = proxyBindingValue.startsWith("profile:")
        ? Number(proxyBindingValue.slice("profile:".length))
        : 0;
      const proxyBindingMode =
        normalizedProxyID > 0 ? "manual" : proxyBindingValue === "none" ? "none" : "auto";
      await apiPost<LaunchSlotSaveResponse>(launchSlotPath(slot.id), {
        ...slot,
        proxy_profile_id: normalizedProxyID,
        proxy_binding_mode: proxyBindingMode,
        proxy_outbound_tag: normalizedProxyID > 0 ? outboundTag : "",
        exit_ip_check_mode: exitIPCheckMode,
      });
      toast.success("已保存槽位代理绑定", {
        description: slot.slot_key,
      });
      refreshProxyQueries(queryClient, agentID || slot.agent_id, {
        delayed: true,
      });
    } catch (err) {
      toast.error("保存槽位代理绑定失败", {
        description: apiErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  }

  async function refreshStatus() {
    setRefreshing(true);
    try {
      const result = await apiPostForm<LaunchSlotRefreshStatusResponse>(
        launchSlotRefreshStatusPath(slot.id),
        {},
      );
      toast.success("已刷新槽位状态", {
        description: result.slot.slot_key || slot.slot_key,
      });
      refreshProxyQueries(queryClient, agentID || slot.agent_id);
    } catch (err) {
      toast.error("刷新槽位状态失败", {
        description: apiErrorMessage(err),
      });
    } finally {
      setRefreshing(false);
    }
  }

  async function probeExitIP() {
    setProbing(true);
    try {
      const result = await apiPostForm<LaunchSlotProbeExitIPResponse>(
        launchSlotProbeExitIPPath(slot.id),
        {},
      );
      if (result.ok) {
        toast.success("已检测出口 IP", {
          description: result.slot.last_exit_ip || slot.slot_key,
        });
      } else {
        toast.error("出口 IP 与预期不一致", {
          description: result.error || result.slot.last_exit_ip || slot.slot_key,
        });
      }
      refreshProxyQueries(queryClient, agentID || slot.agent_id, {
        delayed: true,
      });
    } catch (err) {
      toast.error("检测出口 IP 失败", {
        description: apiErrorMessage(err),
      });
    } finally {
      setProbing(false);
    }
  }

  const normalizedSelectedProxyProfileID = proxyBindingValue.startsWith("profile:")
    ? Number(proxyBindingValue.slice("profile:".length))
    : 0;
  const profile = profiles.find(
    (item) => item.id === normalizedSelectedProxyProfileID,
  );
  const profileOptions = launchSlotProxyProfileOptions(
    profiles,
    normalizedSelectedProxyProfileID,
  );
  const effectiveOutboundTag =
    slot.effective_proxy_outbound_tag || slot.proxy_outbound_tag || "";
  const isProxyManagedSlot =
    normalizedSelectedProxyProfileID > 0 || Boolean(effectiveOutboundTag);
  const probeExitIPAddress =
    slot.probe_exit_ip || (profile ? profile.probe_exit_ip : "") || "";
  const exitIPMismatch =
    Boolean(probeExitIPAddress) &&
    Boolean(slot.last_exit_ip) &&
    probeExitIPAddress !== slot.last_exit_ip;
  return (
    <TableRow>
      <TableCell>
        <div className="font-mono">{slot.slot_key}</div>
        <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
          {slot.expected_game_id || "all games"}
        </div>
      </TableCell>
      <TableCell className="max-w-96 truncate font-mono text-[10px]">
        {slot.resource_root}
      </TableCell>
      <TableCell className="min-w-48">
        <Select
          value={proxyBindingValue}
          onValueChange={setProxyBindingValue}
          disabled={!canWrite || saving}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">自动分配</SelectItem>
            <SelectItem value="none">不绑定</SelectItem>
            {profileOptions.map((profile) => (
              <SelectItem key={profile.id} value={`profile:${profile.id}`}>
                {profile.profile_key}
                {profile.state !== "enabled" ? ` (${profile.state})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {profile ? (
          <div className="mt-1 font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
            {profile.server}:{profile.server_port}
          </div>
        ) : null}
        {profile && profile.state !== "enabled" ? (
          <div className="mt-1 text-[10px] text-[hsl(var(--muted-foreground))]">
            当前代理状态：{profile.state}，启动会被拦截
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        <Input
          value={outboundTag}
          onChange={(e) => setOutboundTag(e.target.value)}
          disabled={!canWrite || saving}
          className="h-8 min-w-36 font-mono text-xs"
          placeholder={`proxy-slot-${slot.slot_key || slot.id}`}
        />
        <div className="mt-1 font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
          {effectiveOutboundTag
            ? `effective: ${effectiveOutboundTag}`
            : "未绑定代理"}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <StatusBadge
            variant={
              !isProxyManagedSlot
                ? "neutral"
                : slot.sing_box_rule_active
                  ? "ok"
                  : "warn"
            }
          >
            {!isProxyManagedSlot
              ? "not managed"
              : slot.sing_box_rule_active
                ? "active"
                : "inactive"}
          </StatusBadge>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
            {isProxyManagedSlot ? slot.sing_box_rule_reason : ""}
          </span>
          {isProxyManagedSlot ? (
            <>
              <StatusBadge variant={appliedGate.variant}>
                {appliedGate.label}
              </StatusBadge>
              <span className="max-w-48 truncate font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                {appliedGate.detail}
              </span>
            </>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <StatusBadge
            variant={
              !isProxyManagedSlot || !slot.last_exit_ip
                ? "neutral"
                : exitIPMismatch
                  ? "warn"
                  : "ok"
            }
          >
            {isProxyManagedSlot ? slot.last_exit_ip || "未检测" : "不适用"}
          </StatusBadge>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
            {isProxyManagedSlot && slot.last_exit_ip_checked_at
              ? formatMs(slot.last_exit_ip_checked_at)
              : ""}
          </span>
          {isProxyManagedSlot && probeExitIPAddress ? (
            <span className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
              probe {probeExitIPAddress}
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <StatusBadge variant={slot.state === "enabled" ? "ok" : "neutral"}>
            {slot.state}
          </StatusBadge>
          <Select
            value={exitIPCheckMode}
            onValueChange={setExitIPCheckMode}
            disabled={!canWrite || saving}
          >
            <SelectTrigger className="h-7 min-w-32 text-[10px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="warn">warn</SelectItem>
              <SelectItem value="enforce">enforce</SelectItem>
              <SelectItem value="disabled">disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={!canWrite || saving}
            onClick={save}
          >
            保存
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canWrite || refreshing}
            onClick={() => void refreshStatus()}
          >
            {refreshing ? "刷新中" : "刷新状态"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canWrite || probing || !isProxyManagedSlot}
            onClick={() => void probeExitIP()}
          >
            {probing ? "检测中" : "检测出口"}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function LaunchSlotCapacityPanel({
  slots,
  gameCapacities,
  canWrite,
}: {
  slots: LaunchSlotEntry[];
  gameCapacities: GameClientCapacityEntry[];
  canWrite: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>槽位容量</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 xl:grid-cols-[360px_1fr]">
          <GameClientCapacityEditor
            capacities={gameCapacities}
            slots={slots}
            canWrite={canWrite}
          />
          <Table dense>
            <TableHeader>
              <TableRow>
                <TableHead>槽位</TableHead>
                <TableHead>当前 / 总上限</TableHead>
                <TableHead>游戏容量覆盖</TableHead>
                <TableHead>已保存覆盖</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slots.map((slot) => (
                <LaunchSlotCapacityRow
                  key={slot.id}
                  slot={slot}
                  gameCapacities={gameCapacities}
                  canWrite={canWrite}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function GameClientCapacityEditor({
  capacities,
  slots,
  canWrite,
}: {
  capacities: GameClientCapacityEntry[];
  slots: LaunchSlotEntry[];
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [gameID, setGameID] = useState(
    capacities[0]?.game_id || slots.find((slot) => slot.expected_game_id)?.expected_game_id || "",
  );
  const [manualDefault, setManualDefault] = useState("");
  const [observedMax, setObservedMax] = useState("");
  const [enforceMode, setEnforceMode] = useState("enforce");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const selected = capacities.find((item) => item.game_id === gameID.trim());

  useEffect(() => {
    if (!gameID && capacities[0]?.game_id) {
      setGameID(capacities[0].game_id);
    }
  }, [capacities, gameID]);

  useEffect(() => {
    setManualDefault(
      selected?.manual_default_max_instances === undefined
        ? ""
        : String(selected.manual_default_max_instances),
    );
    setObservedMax(
      selected?.observed_max_client_count
        ? String(selected.observed_max_client_count)
        : "",
    );
    setEnforceMode(selected?.enforce_mode || "enforce");
    setNote(selected?.note || "");
  }, [
    selected?.enforce_mode,
    selected?.game_id,
    selected?.manual_default_max_instances,
    selected?.note,
    selected?.observed_max_client_count,
  ]);

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const normalizedGameID = gameID.trim();
    if (!normalizedGameID) {
      toast.error("game_id 不能为空");
      return;
    }
    let manual: number | null;
    let observed: number;
    try {
      manual = parseOptionalCapacityValue(manualDefault);
      observed = parseNonNegativeIntValue(observedMax, 0);
    } catch (err) {
      toast.error("容量输入无效", { description: apiErrorMessage(err) });
      return;
    }
    setSaving(true);
    try {
      await apiPost<GameClientCapacitySaveResponse>(
        gameClientCapacityPath(normalizedGameID),
        {
          observed_max_client_count: observed,
          observed_source: selected?.observed_source || "manual",
          observed_at: selected?.observed_at || 0,
          manual_default_max_instances: manual,
          enforce_mode: enforceMode,
          note,
        },
      );
      toast.success("已保存游戏默认容量", { description: normalizedGameID });
      void queryClient.invalidateQueries({
        queryKey: ["game-client-capacities"],
      });
    } catch (err) {
      toast.error("保存游戏默认容量失败", { description: apiErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3 rounded border border-[hsl(var(--border))] p-3 text-xs">
      <div className="font-medium">游戏默认容量</div>
      <form className="space-y-3" onSubmit={save}>
        <label className="block space-y-1">
          <span>game_id</span>
          <Input
            value={gameID}
            onChange={(e) => setGameID(e.target.value)}
            disabled={!canWrite || saving}
            className="h-8 font-mono text-xs"
            placeholder="215335"
            required
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span>手工默认</span>
            <Input
              value={manualDefault}
              onChange={(e) => setManualDefault(e.target.value)}
              disabled={!canWrite || saving}
              className="h-8 font-mono text-xs"
              placeholder="空=继承"
            />
          </label>
          <label className="block space-y-1">
            <span>观测值</span>
            <Input
              value={observedMax}
              onChange={(e) => setObservedMax(e.target.value)}
              disabled={!canWrite || saving}
              className="h-8 font-mono text-xs"
              placeholder="0"
            />
          </label>
        </div>
        <label className="block space-y-1">
          <span>模式</span>
          <Select
            value={enforceMode}
            onValueChange={setEnforceMode}
            disabled={!canWrite || saving}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="enforce">enforce</SelectItem>
              <SelectItem value="warn">warn</SelectItem>
              <SelectItem value="disabled">disabled</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="block space-y-1">
          <span>note</span>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={!canWrite || saving}
            className="h-8 text-xs"
          />
        </label>
        <Button type="submit" size="sm" disabled={!canWrite || saving}>
          保存默认容量
        </Button>
      </form>
      <div className="space-y-1 border-t border-[hsl(var(--border))] pt-3">
        {capacities.length === 0 ? (
          <div className="text-[hsl(var(--muted-foreground))]">
            暂无游戏默认容量
          </div>
        ) : null}
        {capacities.slice(0, 8).map((capacity) => (
          <button
            key={capacity.game_id}
            type="button"
            className="flex w-full items-center justify-between rounded border border-[hsl(var(--border))] px-2 py-1 text-left hover:bg-[hsl(var(--muted))]"
            onClick={() => setGameID(capacity.game_id)}
          >
            <span className="font-mono">{capacity.game_id}</span>
            <span className="text-[hsl(var(--muted-foreground))]">
              {capacityLabel(capacity.manual_default_max_instances)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function LaunchSlotCapacityRow({
  slot,
  gameCapacities,
  canWrite,
}: {
  slot: LaunchSlotEntry;
  gameCapacities: GameClientCapacityEntry[];
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [slotMaxInstances, setSlotMaxInstances] = useState(
    String(slot.max_instances || 1),
  );
  const capacitiesQ = useQuery({
    queryKey: ["slot-game-capacities", slot.id],
    queryFn: () =>
      apiGet<SlotGameCapacitiesResponse>(launchSlotGameCapacitiesPath(slot.id)),
  });
  const defaultGameID =
    slot.expected_game_id || gameCapacities[0]?.game_id || "";
  const [gameID, setGameID] = useState(defaultGameID);
  const [manualMax, setManualMax] = useState("");
  const [enforceMode, setEnforceMode] = useState("enforce");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const selected = capacitiesQ.data?.items.find(
    (item) => item.game_id === gameID.trim(),
  );
  const selectedGameCapacity = gameCapacities.find(
    (item) => item.game_id === gameID.trim(),
  );
  const effectiveCapacity = effectiveCapacityForSlot(
    slot,
    selected,
    selectedGameCapacity,
  );

  useEffect(() => {
    setSlotMaxInstances(String(slot.max_instances || 1));
  }, [slot.id, slot.max_instances]);

  useEffect(() => {
    if (!gameID && defaultGameID) {
      setGameID(defaultGameID);
    }
  }, [defaultGameID, gameID]);

  useEffect(() => {
    setManualMax(
      selected?.manual_max_instances === undefined
        ? ""
        : String(selected.manual_max_instances),
    );
    setEnforceMode(selected?.enforce_mode || "enforce");
    setNote(selected?.note || "");
  }, [
    selected?.enforce_mode,
    selected?.game_id,
    selected?.manual_max_instances,
    selected?.note,
  ]);

  async function save() {
    const normalizedGameID = gameID.trim();
    let maxInstances: number;
    let manual: number | null;
    try {
      maxInstances = parsePositiveIntValue(slotMaxInstances);
      manual = normalizedGameID ? parseOptionalCapacityValue(manualMax) : null;
    } catch (err) {
      toast.error("容量输入无效", { description: apiErrorMessage(err) });
      return;
    }
    setSaving(true);
    try {
      await apiPost<LaunchSlotSaveResponse>(launchSlotPath(slot.id), {
        ...slot,
        max_instances: maxInstances,
      });
      if (normalizedGameID) {
        await apiPost<SlotGameCapacitySaveResponse>(
          launchSlotGameCapacityPath(slot.id, normalizedGameID),
          {
            manual_max_instances: manual,
            enforce_mode: enforceMode,
            note,
          },
        );
        void queryClient.invalidateQueries({
          queryKey: ["slot-game-capacities", slot.id],
        });
      }
      toast.success("已保存槽位容量", {
        description: normalizedGameID
          ? `${slot.slot_key} / ${normalizedGameID}`
          : `${slot.slot_key} / 总上限 ${maxInstances}`,
      });
      void queryClient.invalidateQueries({ queryKey: ["launch-slots"] });
      void queryClient.invalidateQueries({ queryKey: ["machine"] });
    } catch (err) {
      toast.error("保存槽位容量失败", {
        description: apiErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <TableRow>
      <TableCell>
        <div className="font-mono">{slot.slot_key}</div>
        <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
          {slot.resource_root}
        </div>
      </TableCell>
      <TableCell>
        <div className="font-mono text-xs">
          {slot.current_instance_count}/{slot.max_instances}
        </div>
        <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
          {slot.expected_game_id || "all games"}
        </div>
      </TableCell>
      <TableCell className="min-w-[340px]">
        <div className="grid items-end gap-2 md:grid-cols-[80px_90px_80px_105px_auto]">
          <label className="block space-y-1">
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
              总上限
            </span>
            <Input
              value={slotMaxInstances}
              onChange={(e) => setSlotMaxInstances(e.target.value)}
              disabled={!canWrite || saving}
              className="h-8 font-mono text-xs"
              inputMode="numeric"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
              game_id
            </span>
            <Input
              value={gameID}
              onChange={(e) => setGameID(e.target.value)}
              disabled={!canWrite || saving}
              className="h-8 font-mono text-xs"
              placeholder="215335"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
              游戏覆盖
            </span>
            <Input
              value={manualMax}
              onChange={(e) => setManualMax(e.target.value)}
              disabled={!canWrite || saving}
              className="h-8 font-mono text-xs"
              placeholder="空=继承"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
              模式
            </span>
            <Select
              value={enforceMode}
              onValueChange={setEnforceMode}
              disabled={!canWrite || saving}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="enforce">enforce</SelectItem>
                <SelectItem value="warn">warn</SelectItem>
                <SelectItem value="disabled">disabled</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <Button
            type="button"
            size="sm"
            disabled={!canWrite || saving}
            onClick={save}
          >
            保存
          </Button>
        </div>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={!canWrite || saving}
          className="mt-2 h-7 text-[10px]"
          placeholder="note"
        />
        <div className="mt-1 text-[10px] text-[hsl(var(--muted-foreground))]">
          有效游戏容量：
          <span className="font-mono">{effectiveCapacity.label}</span>
          <span className="ml-1">来源：{effectiveCapacity.source}</span>
        </div>
      </TableCell>
      <TableCell>
        {capacitiesQ.isLoading ? (
          <span className="text-xs text-[hsl(var(--muted-foreground))]">
            读取中
          </span>
        ) : capacitiesQ.data?.items.length ? (
          <div className="flex flex-col gap-1 text-[10px]">
            {capacitiesQ.data.items.map((capacity) => (
              <button
                key={capacity.game_id}
                type="button"
                className="flex items-center justify-between gap-2 rounded border border-[hsl(var(--border))] px-2 py-1 text-left hover:bg-[hsl(var(--muted))]"
                onClick={() => setGameID(capacity.game_id)}
              >
                <span className="font-mono">{capacity.game_id}</span>
                <span>{capacityLabel(capacity.manual_max_instances)}</span>
                <span className="text-[hsl(var(--muted-foreground))]">
                  {capacity.enforce_mode}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <span className="text-xs text-[hsl(var(--muted-foreground))]">
            继承游戏默认
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

function capacityLabel(value: number | undefined): string {
  if (value === undefined) return "继承";
  if (value === 0) return "unlimited";
  return String(value);
}

function effectiveCapacityForSlot(
  slot: LaunchSlotEntry,
  slotCapacity: SlotGameCapacityEntry | undefined,
  gameCapacity: GameClientCapacityEntry | undefined,
): { label: string; source: string } {
  if (slotCapacity?.manual_max_instances !== undefined) {
    return {
      label: capacityLabel(slotCapacity.manual_max_instances),
      source: "槽位覆盖",
    };
  }
  if (gameCapacity?.manual_default_max_instances !== undefined) {
    return {
      label: capacityLabel(gameCapacity.manual_default_max_instances),
      source: "游戏手工默认",
    };
  }
  if ((gameCapacity?.observed_max_client_count ?? 0) > 0) {
    return {
      label: String(gameCapacity?.observed_max_client_count),
      source: "客户端观测",
    };
  }
  return {
    label: String(slot.max_instances || 1),
    source: "槽位总上限",
  };
}

function parseOptionalCapacityValue(value: string): number | null {
  const text = value.trim();
  if (!text) return null;
  return parseNonNegativeIntValue(text, 0);
}

function parseNonNegativeIntValue(value: string, fallback: number): number {
  const text = value.trim();
  if (!text) return fallback;
  const parsed = Number(text);
  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new Error("必须是非负整数");
  }
  return parsed;
}

function parsePositiveIntValue(value: string): number {
  const text = value.trim();
  if (!/^\d+$/.test(text)) {
    throw new Error("请输入正整数");
  }
  const parsed = Number.parseInt(text, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    throw new Error("请输入大于等于 1 的整数");
  }
  return parsed;
}

function MarketCDPPanel({
  machine,
  canWrite,
}: {
  machine: MachineRecordEntry;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const accountCDPBindingCount = machine.account_cdp_binding_count ?? 0;
  const accountCDPPaths = machine.account_cdp_box_exe_paths ?? [];
  const accountCDPPorts = machine.account_cdp_ports ?? [];
  const hasAccountCDPBinding = accountCDPBindingCount > 0;
  const boxStatusVariant = machine.box996_installed
    ? "ok"
    : hasAccountCDPBinding
      ? "warn"
      : "danger";
  const boxStatusLabel = machine.box996_installed
    ? "默认 996box.exe 已检测"
    : hasAccountCDPBinding
      ? "账号级 996box 已配置"
      : "缺少 996box.exe";
  const accountCDPDetail = hasAccountCDPBinding
    ? [
        `账号绑定 ${accountCDPBindingCount} 个`,
        accountCDPPaths.length > 0 ? accountCDPPaths.join("；") : "",
        accountCDPPorts.length > 0 ? `端口 ${accountCDPPorts.join(", ")}` : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";
  const cdpDetail =
    accountCDPDetail ||
    machine.box996_exe_path ||
    machine.cdp_blocked_reason ||
    machine.market_cdp_status ||
    "等待 agent 上报能力状态";
  async function toggle(next: boolean) {
    setPending(true);
    try {
      await apiPostForm<MachineMarketCDPResponse>(
        machineMarketCDPPath(machine.id),
        { enabled: next },
      );
      toast.success(next ? "已开启 Python CDP 采集" : "已关闭 Python CDP 采集");
      void queryClient.invalidateQueries({ queryKey: ["machine", String(machine.id)] });
      void queryClient.invalidateQueries({ queryKey: ["market-analysis-agent-capabilities"] });
      void queryClient.invalidateQueries({ queryKey: ["market-analysis-scheduler"] });
    } catch (err) {
      const message =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("更新 Python CDP 开关失败", { description: message });
    } finally {
      setPending(false);
    }
  }
  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>Python CDP 采集</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge variant={machine.market_cdp_enabled ? "ok" : "neutral"}>
                {machine.market_cdp_enabled ? "已开启" : "已关闭"}
              </StatusBadge>
              <StatusBadge variant={boxStatusVariant}>{boxStatusLabel}</StatusBadge>
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              {cdpDetail}
            </div>
            {machine.box996_installed && accountCDPDetail ? (
              <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
                {accountCDPDetail}
              </div>
            ) : null}
            {machine.collector_version ? (
              <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                collector {machine.collector_version}
              </div>
            ) : null}
          </div>
          <Switch
            checked={machine.market_cdp_enabled}
            disabled={!canWrite || pending}
            aria-label="Python CDP 采集开关"
            onCheckedChange={toggle}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function CachesTable({ caches }: { caches: MachineCacheEntry[] }) {
  const session = useSession();
  const actor = session.data?.actor ?? "anonymous";
  const canWrite = session.data?.can_write ?? false;
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [dryRun, setDryRun] = useState(true);
  const [stubVersion, setStubVersion] = useState("");
  const [jobID, setJobID] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [serverConfirm, setServerConfirm] = useState<{
    nonce: string;
    target: string;
  } | null>(null);

  const selectedCaches = caches.filter((c) => selected.has(c.id));
  const allChecked = caches.length > 0 && selected.size === caches.length;

  function toggleAll(next: boolean) {
    setSelected(next ? new Set(caches.map((c) => c.id)) : new Set());
  }

  function toggleOne(id: number, next: boolean) {
    setSelected((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  }

  function confirmTarget() {
    return `cache-batch:${selectedCaches.length}`;
  }

  async function submit(confirm?: { nonce: string; target: string }) {
    if (selectedCaches.length === 0) {
      toast.error("至少选择一个 cache");
      return;
    }
    setPending(true);
    try {
      const body = new URLSearchParams();
      for (const cache of selectedCaches) body.append("cache_id", String(cache.id));
      body.set("dry_run", dryRun ? "1" : "0");
      if (stubVersion.trim()) body.set("stub_version", stubVersion.trim());
      if (confirm) {
        body.set("confirm_target", confirm.target);
        body.set("confirm_nonce", confirm.nonce);
        body.set("confirm_text", confirm.target);
      }
      const res = await apiPostURLSearchParams<{ ok: boolean; job_id: string }>(
        cachePolicyBatchApplyPath,
        body,
      );
      setJobID(res.job_id);
      toast.success(`已创建 batch apply job：${res.job_id}`);
      void queryClient.invalidateQueries({ queryKey: ["machine-caches"] });
    } catch (err) {
      const message =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("batch apply 失败", { description: message });
      throw err;
    } finally {
      setPending(false);
    }
  }

  function openConfirm() {
    if (selectedCaches.length === 0) {
      toast.error("至少选择一个 cache");
      return;
    }
    if (selectedCaches.length > 20) {
      const target = confirmTarget();
      setPending(true);
      requestConfirmNonce(`batch_apply:${target}`, target)
        .then((res) => {
          setServerConfirm({ nonce: res.nonce, target: res.target });
          setConfirmOpen(true);
        })
        .catch((err) => {
          const message =
            err instanceof APIError
              ? err.message
              : err instanceof Error
                ? err.message
                : String(err);
          toast.error("申请确认 nonce 失败", { description: message });
        })
        .finally(() => setPending(false));
      return;
    }
    setServerConfirm(null);
    setConfirmOpen(true);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 text-xs">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
          />
          dry run
        </label>
        <label className="space-y-1">
          <span className="block text-[hsl(var(--muted-foreground))]">
            stub_version
          </span>
          <input
            value={stubVersion}
            onChange={(e) => setStubVersion(e.target.value)}
            className="h-8 rounded border border-[hsl(var(--input))] bg-transparent px-2"
            placeholder="留空使用策略值"
          />
        </label>
        <Button
          size="sm"
          disabled={!canWrite || pending || selectedCaches.length === 0}
          onClick={openConfirm}
        >
          批量 apply ({selectedCaches.length})
        </Button>
      </div>
      {jobID ? (
        <JobStatus
          jobID={jobID}
          onCompleted={() =>
            void queryClient.invalidateQueries({ queryKey: ["machine-caches"] })
          }
        />
      ) : null}
      <Table dense>
        <TableHeader>
          <TableRow>
            <TableHead>
              <input
                type="checkbox"
                checked={allChecked}
                onChange={(e) => toggleAll(e.target.checked)}
                aria-label="全选 cache"
              />
            </TableHead>
            <TableHead>cache ID</TableHead>
            <TableHead>游戏</TableHead>
            <TableHead>cq2.exe 目录</TableHead>
            <TableHead>cache 路径</TableHead>
            <TableHead>dev</TableHead>
            <TableHead>stub</TableHead>
            <TableHead>已应用状态</TableHead>
            <TableHead>最近错误</TableHead>
            <TableHead>操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {caches.map((c) => (
            <CacheRow
              key={c.id}
              cache={c}
              canWrite={canWrite}
              selected={selected.has(c.id)}
              onToggle={(next) => toggleOne(c.id, next)}
            />
          ))}
        </TableBody>
      </Table>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        resource="machine_cache"
        action="batch_apply"
        target={serverConfirm?.target ?? `${selectedCaches.length}-caches`}
        actor={actor}
        sig={serverConfirm?.nonce}
        description={`对 ${selectedCaches.length} 个 cache 执行 apply`}
        onConfirm={() => submit(serverConfirm ?? undefined)}
      />
    </div>
  );
}

interface CacheRowProps {
  cache: MachineCacheEntry;
  canWrite: boolean;
  selected: boolean;
  onToggle: (next: boolean) => void;
}

function CacheRow({
  cache,
  canWrite,
  selected,
  onToggle,
}: CacheRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [reinstalling, setReinstalling] = useState(false);
  const queryClient = useQueryClient();

  async function reinstallLauncher() {
    setReinstalling(true);
    try {
      const body: Record<string, string> = {
        install_only: "1",
        dry_run: "0",
      };
      if (cache.resource_root?.trim()) {
        body.resource_root = cache.resource_root.trim();
      }
      await apiPostForm(cachePolicyApplyPath(cache.id), body);
      toast.success("已派发 launcher 重装", { description: cache.cache_root });
      void queryClient.invalidateQueries({ queryKey: ["machine-caches"] });
      void queryClient.invalidateQueries({ queryKey: ["machine"] });
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("重装 launcher 失败", { description: msg });
    } finally {
      setReinstalling(false);
    }
  }

  return (
    <>
      <TableRow>
        <TableCell>
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onToggle(e.target.checked)}
            aria-label={`选择 ${cache.cache_id}`}
          />
        </TableCell>
        <TableCell className="font-mono">{cache.cache_id}</TableCell>
        <TableCell>{cache.game_name ?? cache.game_id ?? ""}</TableCell>
        <TableCell className="font-mono text-[10px]">
          {cache.resource_root ?? ""}
        </TableCell>
        <TableCell className="font-mono text-[10px]">
          {cache.cache_root}
        </TableCell>
        <TableCell>
          <StatusBadge variant={cache.dev_enabled ? "warn" : "neutral"}>
            {cache.dev_enabled ? "dev" : "off"}
          </StatusBadge>
        </TableCell>
        <TableCell className="font-mono">
          {cache.installed_stub_version ?? ""}
        </TableCell>
        <TableCell>{cache.policy.applied_state ?? ""}</TableCell>
        <TableCell className="text-[hsl(var(--muted-foreground))]">
          {cache.policy.last_apply_error ?? ""}
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={!canWrite || reinstalling}
              onClick={() => void reinstallLauncher()}
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              {reinstalling ? "重装中…" : "重装 launcher"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "收起" : "编辑策略"}
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow>
          <TableCell colSpan={10} className="bg-[hsl(var(--muted))]/30">
            <CachePolicyEditor cache={cache} canWrite={canWrite} />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

interface CachePolicyEditorProps {
  cache: MachineCacheEntry;
  canWrite: boolean;
}

function CachePolicyEditor({ cache, canWrite }: CachePolicyEditorProps) {
  const queryClient = useQueryClient();
  const policy = cache.policy;
  const [luaSealed, setLuaSealed] = useState(policy.lua_sealed_stub_enabled);
  const [socketDev, setSocketDev] = useState(policy.socket_dev_modules_enabled);
  const [stubVersion, setStubVersion] = useState(policy.stub_version ?? "");
  const [daemonHost, setDaemonHost] = useState(policy.daemon_lua_host ?? "");
  const [daemonPort, setDaemonPort] = useState(
    policy.daemon_lua_port ? String(policy.daemon_lua_port) : "",
  );

  const [applyDryRun, setApplyDryRun] = useState(true);
  const [applyStub, setApplyStub] = useState(policy.stub_version ?? "");
  const [applyResourceRoot, setApplyResourceRoot] = useState("");

  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [applying, setApplying] = useState(false);
  const source = policy.source || "none";

  async function saveDesired() {
    setSaving(true);
    try {
      const body: Record<string, string> = {
        stub_version: stubVersion,
        daemon_lua_host: daemonHost,
        daemon_lua_port: daemonPort,
      };
      if (luaSealed) body.lua_sealed_stub_enabled = "1";
      if (socketDev) body.socket_dev_modules_enabled = "1";
      await apiPostForm(cachePolicyPath(cache.id), body);
      toast.success("已保存期望策略");
      void queryClient.invalidateQueries({ queryKey: ["machine-caches"] });
      void queryClient.invalidateQueries({ queryKey: ["machine"] });
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("保存失败", { description: msg });
    } finally {
      setSaving(false);
    }
  }

  async function clearOverride() {
    setClearing(true);
    try {
      await apiPostForm(cachePolicyClearPath(cache.id), {});
      toast.success("已清除 cache 覆盖", { description: "该 cache 将继承全局策略" });
      void queryClient.invalidateQueries({ queryKey: ["machine-caches"] });
      void queryClient.invalidateQueries({ queryKey: ["machine"] });
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("清除失败", { description: msg });
    } finally {
      setClearing(false);
    }
  }

  async function applyPolicy(confirm?: { nonce: string; target: string }) {
    setApplying(true);
    try {
      const body: Record<string, string> = {};
      if (applyStub.trim()) body.stub_version = applyStub.trim();
      if (applyResourceRoot.trim()) body.resource_root = applyResourceRoot.trim();
      if (applyDryRun) body.dry_run = "1";
      if (confirm) {
        body.confirm_nonce = confirm.nonce;
        body.confirm_text = confirm.target;
      }
      await apiPostForm(cachePolicyApplyPath(cache.id), body);
      toast.success("已派发 apply", { description: cache.cache_root });
      void queryClient.invalidateQueries({ queryKey: ["machine-caches"] });
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("apply 失败", { description: msg });
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="grid gap-4 p-2 text-xs lg:grid-cols-2">
      <div className="space-y-2 rounded border border-[hsl(var(--border))] p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium">
            {source === "global" ? "期望策略（继承全局）" : "期望策略（cache 覆盖）"}
          </div>
          <Badge variant={source === "cache" ? "secondary" : "outline"}>
            {source === "cache" ? "cache 覆盖" : source === "global" ? "全局继承" : "未配置"}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2">
            <Checkbox
              checked={luaSealed}
              onCheckedChange={(c) => setLuaSealed(c === true)}
              disabled={!canWrite}
              aria-label="lua_sealed_stub_enabled"
            />
            <span>sealed stub</span>
          </label>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={socketDev}
              onCheckedChange={(c) => setSocketDev(c === true)}
              disabled={!canWrite}
              aria-label="socket_dev_modules_enabled"
            />
            <span>socket dev modules</span>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label>
            <span className="block text-[hsl(var(--muted-foreground))]">
              stub_version
            </span>
            <Input
              value={stubVersion}
              onChange={(e) => setStubVersion(e.target.value)}
              disabled={!canWrite}
              className="h-7"
            />
          </label>
          <label>
            <span className="block text-[hsl(var(--muted-foreground))]">
              daemon_lua_host
            </span>
            <Input
              value={daemonHost}
              onChange={(e) => setDaemonHost(e.target.value)}
              disabled={!canWrite}
              placeholder="8173 host"
              className="h-7"
            />
          </label>
          <label>
            <span className="block text-[hsl(var(--muted-foreground))]">
              daemon_lua_port
            </span>
            <Input
              value={daemonPort}
              onChange={(e) => setDaemonPort(e.target.value)}
              disabled={!canWrite}
              placeholder="8173 port"
              className="h-7"
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={!canWrite || saving}
            onClick={() => void saveDesired()}
          >
            {saving ? "保存中…" : source === "global" ? "保存为 cache 覆盖" : "保存期望"}
          </Button>
          {source === "cache" ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!canWrite || clearing}
              onClick={() => void clearOverride()}
            >
              {clearing ? "清除中…" : "清除覆盖"}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="space-y-2 rounded border border-[hsl(var(--border))] p-3">
        <div className="font-medium">单行 apply</div>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2">
            <Checkbox
              checked={applyDryRun}
              onCheckedChange={(c) => setApplyDryRun(c === true)}
              disabled={!canWrite}
              aria-label="apply-dry-run"
            />
            <span>dry_run</span>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label>
            <span className="block text-[hsl(var(--muted-foreground))]">
              stub_version（覆盖期望）
            </span>
            <Input
              value={applyStub}
              onChange={(e) => setApplyStub(e.target.value)}
              disabled={!canWrite}
              className="h-7"
            />
          </label>
          <label>
            <span className="block text-[hsl(var(--muted-foreground))]">
              resource_root（可空）
            </span>
            <Input
              value={applyResourceRoot}
              onChange={(e) => setApplyResourceRoot(e.target.value)}
              disabled={!canWrite}
              className="h-7"
            />
          </label>
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!canWrite || applying}
          onClick={() => void applyPolicy()}
        >
          {applying ? "派发中…" : "应用策略"}
        </Button>
      </div>
    </div>
  );
}

interface AuthsTableProps {
  auths: MachineListEntry[];
  actor: string;
  canWrite: boolean;
  machineID: string;
}

function AuthsTable({ auths, canWrite, actor, machineID }: AuthsTableProps) {
  const queryClient = useQueryClient();

  async function saveMetadata(authID: number, note: string, tags: string) {
    try {
      await apiPostForm(machineAuthMetadataPath(authID), { note, tags });
      toast.success("已保存");
      void queryClient.invalidateQueries({ queryKey: ["machine", machineID] });
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("保存失败", { description: msg });
      throw err;
    }
  }

  if (auths.length === 0) {
    return (
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        该机器没有 auth 记录。Windows agent 首次连接 8174 会自动写一行 pending 等审批。
      </p>
    );
  }

  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>授权 ID</TableHead>
          <TableHead>mac_id</TableHead>
          <TableHead>状态</TableHead>
          <TableHead>备注</TableHead>
          <TableHead>标签</TableHead>
          <TableHead>最后出现</TableHead>
          <TableHead>操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {auths.map((a) => (
          <TableRow key={a.id}>
            <TableCell className="font-mono">{a.id}</TableCell>
            <TableCell className="font-mono">{a.mac_id}</TableCell>
            <TableCell>
              <StatusBadge
                variant={
                  a.status === "active" || a.status === "verified"
                    ? "ok"
                    : a.status === "pending"
                      ? "warn"
                      : a.status === "revoked"
                        ? "danger"
                        : "neutral"
                }
              >
                {a.status}
              </StatusBadge>
            </TableCell>
            <TableCell>
              <InlineEditableCell
                value={a.note ?? ""}
                disabled={!canWrite}
                ariaLabel={`note-${a.id}`}
                onSave={(next) => saveMetadata(a.id, next, a.tags ?? "")}
              />
            </TableCell>
            <TableCell>
              <InlineEditableCell
                value={a.tags ?? ""}
                disabled={!canWrite}
                ariaLabel={`tags-${a.id}`}
                onSave={(next) => saveMetadata(a.id, a.note ?? "", next)}
              />
            </TableCell>
            <TableCell className="text-[hsl(var(--muted-foreground))]">
              {a.last_seen_at ? formatMs(a.last_seen_at) : ""}
            </TableCell>
            <TableCell>
              <AuthActionsCell
                domain="machine"
                authID={a.id}
                status={a.status}
                subject={a.subject}
                actor={actor}
                canWrite={canWrite}
                onChanged={() =>
                  void queryClient.invalidateQueries({
                    queryKey: ["machine", machineID],
                  })
                }
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
